import { formatMoney } from '@/utils/currency';
import QRCode from 'qrcode';

export type PickupListSale = {
  name: string;
  invoice_date?: string;
  creation?: string;
  payment_method?: string;
  notes?: string;
  customer?: {
    customer_name?: string;
    name?: string;
    mobile?: string;
    address?: string;
    city?: string;
    postal_code?: string;
  };
  items: Array<{
    item_code?: string;
    item_name?: string;
    quantity: number;
    rate?: number;
    amount?: number;
    barcode?: string;
  }>;
  financials?: {
    total?: number;
    subtotal?: number;
  };
};

export type PickupListOptions = {
  includePrice: boolean;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyLogo?: string;
  /** Absolute URL base for QR deep-links, e.g. https://app.example.com */
  appOrigin?: string;
};

/** A4 content width at 96dpi (approx). */
const PAGE_CSS_WIDTH = 794;
/** Conservative rows per A4 page (header + footer reserved). */
function rowsPerPage(includePrice: boolean) {
  return includePrice ? 18 : 22;
}

/** Build a URL that opens this invoice on the Sales tab. */
export function buildSaleOpenUrl(invoiceName: string, origin?: string): string {
  const base =
    (origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return `${base}/sales?tab=sales&invoice=${encodeURIComponent(invoiceName)}`;
}

/**
 * Parse a scanned QR/barcode into an invoice id.
 * Accepts: full sales URL, SALEDESK:INV:xxx, or a bare invoice name.
 */
export function parseSaleScanCode(raw: string): string | null {
  const code = (raw || '').trim();
  if (!code) return null;

  try {
    if (/^https?:\/\//i.test(code) || code.includes('/sales?')) {
      const url = new URL(code, typeof window !== 'undefined' ? window.location.origin : 'http://local');
      const inv =
        url.searchParams.get('invoice') ||
        url.searchParams.get('open') ||
        url.searchParams.get('search');
      if (inv?.trim()) return inv.trim();
    }
  } catch {
    /* ignore */
  }

  const token = code.match(/^SALEDESK:INV:(.+)$/i);
  if (token?.[1]) return token[1].trim();

  if (/^[A-Za-z0-9][A-Za-z0-9._\-\/]{2,64}$/.test(code) && !/\s/.test(code)) {
    return code;
  }
  return null;
}

/** Reject profile junk that leaked JS/source into address fields. */
export function sanitizePrintText(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (
    /\b(const|let|var|function|import|export|return)\b/.test(s) ||
    /[{};=<>]/.test(s) ||
    /\?.*:/.test(s) ||
    /subtotalDisplay|promoTotals|showPromoRow|frappe\./i.test(s)
  ) {
    return '';
  }
  return s;
}

/** Auto-built metadata lines stuffed into sale.notes — never show these on pick lists. */
const AUTO_NOTE_PREFIXES = [
  /^order type\s*:/i,
  /^delivery date\s*:/i,
  /^payment term\s*:/i,
  /^contact name\s*:/i,
  /^billing address\s*:/i,
  /^delivery address\s*:/i,
  /^discount\s*:/i,
];

/**
 * Extract only the user-typed Notes field from combined sale.notes.
 * Ignores Order Type / Delivery Date / Payment Term / Address / Discount lines.
 */
export function extractUserPickupNotes(raw: unknown): string {
  const text = sanitizePrintText(raw);
  if (!text) return '';

  // Prefer explicit "Notes: …" block (may be multi-line until next auto field)
  const notesMatch = text.match(/(?:^|\n)Notes:\s*([\s\S]*)$/i);
  if (notesMatch) {
    const after = notesMatch[1] || '';
    const lines: string[] = [];
    for (const line of after.split(/\r?\n/)) {
      if (AUTO_NOTE_PREFIXES.some((re) => re.test(line.trim()))) break;
      lines.push(line);
    }
    return lines.join('\n').trim();
  }

  // Otherwise keep only non-auto lines (plain user notes without "Notes:" prefix)
  const kept = text
    .split(/\r?\n/)
    .map((l) => l.trimEnd())
    .filter((l) => {
      const t = l.trim();
      if (!t) return false;
      return !AUTO_NOTE_PREFIXES.some((re) => re.test(t));
    });
  return kept.join('\n').trim();
}

function escapeHtml(s: unknown) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatUkDate(value?: string) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return escapeHtml(String(value).split(' ')[0]);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function chunkItems<T>(items: T[], size: number): T[][] {
  if (!items.length) return [[]];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

async function qrDataUrl(invoiceName: string, origin?: string): Promise<string> {
  const url = buildSaleOpenUrl(invoiceName, origin);
  // Compact QR — smaller bytes in PDF
  return QRCode.toDataURL(url, {
    margin: 1,
    width: 96,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#ffffff' },
  });
}

/** Downscale / recompress logo to keep PDF lean. */
async function optimizeLogoDataUrl(src?: string): Promise<string> {
  if (!src) return '';
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = src;
    });
    const maxW = 160;
    const maxH = 56;
    const scale = Math.min(1, maxW / img.width, maxH / img.height);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch {
    return src;
  }
}

function buildOrderPage(
  sale: PickupListSale,
  opts: PickupListOptions & { logoSrc?: string },
  qrSrc: string,
  pageItems: PickupListSale['items'],
  itemOffset: number,
  pageIndex: number,
  pageCount: number,
) {
  const company = sanitizePrintText(opts.companyName) || 'Company';
  const address = sanitizePrintText(opts.companyAddress);
  const phone = sanitizePrintText(opts.companyPhone);
  const logo = opts.logoSrc
    ? `<img src="${escapeHtml(opts.logoSrc)}" alt="" style="max-height:40px;max-width:120px;object-fit:contain;margin-bottom:4px;" />`
    : '';
  const includePrice = opts.includePrice;
  const c = sale.customer || {};
  const customerName = sanitizePrintText(c.customer_name || c.name) || 'Walk-in Customer';
  const customerContact = [
    sanitizePrintText(c.mobile),
    sanitizePrintText([c.address, c.city, c.postal_code].filter(Boolean).join(', ')),
  ]
    .filter(Boolean)
    .join(' · ');
  const allItems = sale.items || [];
  const totalUnits = allItems.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const orderTotal = sale.financials?.total ?? sale.financials?.subtotal;
  const colCount = includePrice ? 7 : 5;

  const rows = pageItems
    .map((it, idx) => {
      const qty = Number(it.quantity) || 0;
      const rate = Number(it.rate) || 0;
      const amount = Number(it.amount) || qty * rate;
      const code = it.item_code || '';
      return `
        <tr>
          <td style="padding:7px 5px;text-align:center;border-bottom:1px solid #ccc;vertical-align:middle;width:36px;">
            <span style="display:inline-block;width:16px;height:16px;border:1.5px solid #111;border-radius:2px;"></span>
          </td>
          <td style="padding:7px 5px;text-align:center;border-bottom:1px solid #ccc;font-size:10px;width:28px;">${itemOffset + idx + 1}</td>
          <td style="padding:7px 5px;border-bottom:1px solid #ccc;font-family:ui-monospace,monospace;font-size:10px;width:90px;">${escapeHtml(code)}</td>
          <td style="padding:7px 5px;border-bottom:1px solid #ccc;font-size:11px;">${escapeHtml(it.item_name || code || 'Item')}</td>
          <td style="padding:7px 5px;text-align:center;border-bottom:1px solid #ccc;font-size:13px;font-weight:700;width:44px;">${escapeHtml(qty)}</td>
          ${
            includePrice
              ? `
          <td style="padding:7px 5px;text-align:right;border-bottom:1px solid #ccc;font-size:10px;width:64px;">${escapeHtml(formatMoney(rate))}</td>
          <td style="padding:7px 5px;text-align:right;border-bottom:1px solid #ccc;font-size:10px;font-weight:600;width:72px;">${escapeHtml(formatMoney(amount))}</td>`
              : ''
          }
        </tr>`;
    })
    .join('');

  return `
    <section class="pickup-page" data-order="${escapeHtml(sale.name)}" data-page="${pageIndex + 1}" data-pages="${pageCount}" style="width:${PAGE_CSS_WIDTH}px;box-sizing:border-box;padding:22px 28px 18px;background:#fff;color:#111;font-family:Arial,Helvetica,sans-serif;display:flex;flex-direction:column;min-height:1080px;">
      <div style="flex:1 1 auto;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:12px;padding-bottom:10px;border-bottom:2px solid #111;">
          <div style="min-width:0;flex:1;">
            ${logo}
            <div style="font-size:16px;font-weight:700;">${escapeHtml(company)}</div>
            <div style="font-size:10px;line-height:1.35;margin-top:2px;">
              ${address ? `${escapeHtml(address)}<br>` : ''}
              ${phone ? `Tel: ${escapeHtml(phone)}` : ''}
            </div>
          </div>
          <div style="text-align:center;flex-shrink:0;">
            <img src="${escapeHtml(qrSrc)}" alt="QR" width="80" height="80" style="display:block;margin:0 auto 3px;" />
            <div style="font-size:8px;line-height:1.2;">Scan to open order</div>
          </div>
          <div style="text-align:right;flex-shrink:0;min-width:140px;">
            <div style="font-size:20px;font-weight:700;letter-spacing:0.02em;">PICK LIST</div>
            <div style="margin-top:6px;font-size:11px;line-height:1.4;">
              <div>Order: <strong style="font-family:monospace;">${escapeHtml(sale.name)}</strong></div>
              <div>Date: <strong>${formatUkDate(sale.invoice_date || sale.creation)}</strong></div>
              ${sale.payment_method ? `<div>Payment: <strong>${escapeHtml(sale.payment_method)}</strong></div>` : ''}
            </div>
          </div>
        </div>

        <table style="width:100%;border-collapse:collapse;margin-bottom:10px;font-size:11px;">
          <tr>
            <td style="width:55%;vertical-align:top;padding-right:14px;">
              <div style="font-size:9px;font-weight:700;margin-bottom:2px;text-transform:uppercase;letter-spacing:0.03em;">Customer</div>
              <div style="font-weight:700;">${escapeHtml(customerName)}</div>
              ${customerContact ? `<div style="font-size:10px;margin-top:2px;">${escapeHtml(customerContact)}</div>` : ''}
            </td>
            <td style="width:45%;vertical-align:top;">
              <div style="font-size:9px;font-weight:700;margin-bottom:2px;text-transform:uppercase;letter-spacing:0.03em;">Summary</div>
              <div>Lines: <strong>${allItems.length}</strong> &nbsp;·&nbsp; Units: <strong>${totalUnits}</strong>${
                includePrice && orderTotal != null
                  ? ` &nbsp;·&nbsp; Total: <strong>${escapeHtml(formatMoney(orderTotal))}</strong>`
                  : ''
              }</div>
              <div style="margin-top:5px;font-size:10px;">Picker: ______________ &nbsp; Started: ____ &nbsp; Finished: ____</div>
            </td>
          </tr>
        </table>

        ${
          (() => {
            const userNote = extractUserPickupNotes(sale.notes);
            if (!userNote) return '';
            return `<div style="margin-bottom:10px;font-size:11px;padding:6px 8px;border:1px solid #ccc;border-radius:4px;background:#fafafa;white-space:pre-wrap;"><strong>Notes:</strong> ${escapeHtml(userNote)}</div>`;
          })()
        }

        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="padding:6px 5px;text-align:center;font-size:9px;font-weight:700;border-bottom:2px solid #111;width:36px;">✓</th>
              <th style="padding:6px 5px;text-align:center;font-size:9px;font-weight:700;border-bottom:2px solid #111;width:28px;">#</th>
              <th style="padding:6px 5px;text-align:left;font-size:9px;font-weight:700;border-bottom:2px solid #111;width:90px;">SKU</th>
              <th style="padding:6px 5px;text-align:left;font-size:9px;font-weight:700;border-bottom:2px solid #111;">Description</th>
              <th style="padding:6px 5px;text-align:center;font-size:9px;font-weight:700;border-bottom:2px solid #111;width:44px;">Qty</th>
              ${
                includePrice
                  ? `
              <th style="padding:6px 5px;text-align:right;font-size:9px;font-weight:700;border-bottom:2px solid #111;width:64px;">Price</th>
              <th style="padding:6px 5px;text-align:right;font-size:9px;font-weight:700;border-bottom:2px solid #111;width:72px;">Amount</th>`
                  : ''
              }
            </tr>
          </thead>
          <tbody>
            ${
              rows ||
              `<tr><td colspan="${colCount}" style="padding:16px;text-align:center;">No items</td></tr>`
            }
          </tbody>
        </table>
      </div>

      <div style="margin-top:16px;padding-top:8px;border-top:1px solid #111;font-size:10px;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;gap:10px;margin-bottom:6px;">
          <span>Picked by: ____________________</span>
          <span>Checked by: ____________________</span>
          <span>Time: __________</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:9px;">
          <span>Shortages: ____________________________________________</span>
          <span style="font-weight:700;">Page ${pageIndex + 1} of ${pageCount}</span>
        </div>
      </div>
    </section>
  `;
}

export async function generatePickupListHtml(
  sales: PickupListSale[],
  opts: PickupListOptions,
): Promise<string> {
  const origin = opts.appOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
  const logoSrc = await optimizeLogoDataUrl(opts.companyLogo);
  const perPage = rowsPerPage(opts.includePrice);
  const pages: string[] = [];

  for (const sale of sales) {
    const items = sale.items || [];
    const chunks = chunkItems(items, perPage);
    const pageCount = chunks.length;
    const qr = await qrDataUrl(sale.name, origin);
    chunks.forEach((chunk, pageIdx) => {
      pages.push(
        buildOrderPage(
          sale,
          { ...opts, logoSrc },
          qr,
          chunk,
          pageIdx * perPage,
          pageIdx,
          pageCount,
        ),
      );
    });
  }

  return `<div id="pickup-list-root">${pages.join('')}</div>`;
}

function wrapPrintDocument(bodyHtml: string, title: string) {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 portrait; margin: 8mm; }
    html, body { margin: 0; padding: 0; background: #fff; }
    .pickup-page {
      page-break-after: always;
      break-after: page;
      width: auto !important;
      min-height: auto !important;
      box-sizing: border-box;
    }
    .pickup-page:last-child { page-break-after: auto; break-after: auto; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; break-inside: avoid; }
  </style>
</head>
<body>${bodyHtml}</body>
</html>`;
}

async function canvasToJpegDataUrl(canvas: HTMLCanvasElement, quality = 0.92): Promise<string> {
  return canvas.toDataURL('image/jpeg', quality);
}

export async function downloadPickupListPdf(sales: PickupListSale[], opts: PickupListOptions) {
  if (!sales.length) throw new Error('No sales selected');

  const [html2canvas, { jsPDF }] = await Promise.all([
    import('html2canvas').then((m) => m.default),
    import('jspdf'),
  ]);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.zIndex = '-1';
  container.innerHTML = await generatePickupListHtml(sales, opts);
  document.body.appendChild(container);

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pages = Array.from(container.querySelectorAll('.pickup-page')) as HTMLElement[];

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: PAGE_CSS_WIDTH,
        windowWidth: PAGE_CSS_WIDTH,
        logging: false,
      });
      const imgData = await canvasToJpegDataUrl(canvas, 0.95);
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      if (i > 0) pdf.addPage();
      // Fit within A4; do not shrink multi-page content to a single page
      const drawH = Math.min(imgHeight, pageHeight);
      const drawW = imgWidth * (drawH / imgHeight);
      const x = (pageWidth - drawW) / 2;
      pdf.addImage(imgData, 'JPEG', x, 0, drawW, drawH, undefined, 'MEDIUM');
    }

    const suffix = opts.includePrice ? 'with-prices' : 'no-prices';
    const stamp = new Date().toISOString().slice(0, 10);
    const label =
      sales.length === 1
        ? `PickList_${sales[0].name}_${suffix}.pdf`
        : `PickList_bulk_${sales.length}_${suffix}_${stamp}.pdf`;
    pdf.save(label);
  } finally {
    document.body.removeChild(container);
  }
}

export async function printPickupList(sales: PickupListSale[], opts: PickupListOptions) {
  if (!sales.length) throw new Error('No sales selected');
  const body = await generatePickupListHtml(sales, opts);
  const title =
    sales.length === 1 ? `Pick List ${sales[0].name}` : `Pick Lists (${sales.length})`;
  const win = window.open('', '_blank');
  if (!win) throw new Error('Popup blocked — allow popups to print pick lists');
  win.document.write(wrapPrintDocument(body, title));
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 400);
}
